import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  BarChart3,
  CheckCircle2,
  ClipboardList,
  Clock3,
  FileSpreadsheet,
  HardHat,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { inspectionsAPI, supervisorAPI } from "../../../services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";

const norm = (value) => (value || "").toString().toLowerCase();

const isOperationalLane = (lane) => {
  const name = norm(lane?.lane_name).trim();
  return lane?.lane_id !== null && lane?.lane_id !== undefined && Number(lane.lane_id) >= 0 && name !== "leadership / unscoped";
};

const healthBadgeClass = (health) => {
  switch ((health || "").toLowerCase()) {
    case "very good":
      return "border-emerald-200 bg-emerald-100 text-emerald-800";
    case "good":
      return "border-green-200 bg-green-50 text-green-700";
    case "needs attention":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "at risk":
      return "border-red-200 bg-red-50 text-red-700";
    default:
      return "border-gray-200 bg-gray-50 text-gray-600";
  }
};

const formatRole = (raw) =>
  (raw || "Team member")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getUserName = (user) => {
  const full = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  return full || user?.email || "there";
};

const titleCase = (value) =>
  (value || "Not set")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const reviewDecisionLabel = (value) => {
  const s = norm(value);
  if (["approved", "reviewed", "ready_for_closeout"].includes(s)) return "Approved / Closed";
  if (s === "needs_further_action") return "Needs Further Action";
  return value ? titleCase(value) : "Not reviewed";
};

const isPastDate = (value) => {
  if (!value) return false;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const percent = (value, total) => {
  const n = Number(value || 0);
  const d = Number(total || 0);
  if (!d) return 0;
  return Math.min(100, Math.round((n / d) * 100));
};

const KPI_STYLES = {
  orange: {
    card: "from-orange-50 via-white to-white border-orange-100",
    icon: "bg-orange-100 text-orange-600",
    accent: "bg-orange-500",
  },
  blue: {
    card: "from-blue-50 via-white to-white border-blue-100",
    icon: "bg-blue-100 text-blue-600",
    accent: "bg-blue-500",
  },
  emerald: {
    card: "from-emerald-50 via-white to-white border-emerald-100",
    icon: "bg-emerald-100 text-emerald-600",
    accent: "bg-emerald-500",
  },
  red: {
    card: "from-red-50 via-white to-white border-red-100",
    icon: "bg-red-100 text-red-600",
    accent: "bg-red-500",
  },
};

function KpiCard({ label, value, subtitle, icon: Icon, tone = "orange", onClick }) {
  const style = KPI_STYLES[tone] || KPI_STYLES.orange;
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`relative w-full overflow-hidden rounded-2xl border bg-gradient-to-br p-4 text-left shadow-sm transition ${style.card} ${onClick ? "hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400" : ""}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1.5 ${style.accent}`} />
      <div className="flex items-start justify-between gap-3 pl-1">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{value ?? 0}</div>
          <div className="mt-1 text-xs text-gray-600">{subtitle}</div>
          {onClick ? <div className="mt-3 text-xs font-semibold text-orange-700">Open scoped issues →</div> : null}
        </div>
        {Icon && (
          <div className={`rounded-2xl p-3 ${style.icon}`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Component>
  );
}

function ProgressRow({
  label,
  value,
  total,
  tone = "bg-orange-500",
  rightLabel,
}) {
  const p = percent(value, total);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3 text-xs">
        <span className="truncate font-medium text-gray-700">{label}</span>
        <span className="shrink-0 text-gray-500">
          {rightLabel || `${value || 0} / ${total || 0}`}
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${tone}`}
          style={{ width: `${p}%` }}
        />
      </div>
    </div>
  );
}

function DonutSummary({ total, active, open, needReview, closed, overdue }) {
  const totalCount = Number(total || 0);
  const activeTotal = Number(active || 0);
  const overduePct = percent(overdue, Math.max(activeTotal, 1));

  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Issue Workflow Overview</h3>
          <p className="text-xs text-gray-500">Total = Active + Closed. Active work is split into Open and Need Review.</p>
        </div>
        <BarChart3 className="h-5 w-5 text-orange-500" />
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-[180px_1fr] md:items-center">
        <div className="mx-auto flex h-40 w-40 items-center justify-center rounded-full bg-gradient-to-br from-orange-100 via-blue-100 to-emerald-100 p-3">
          <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full bg-white shadow-inner">
            <div className="text-3xl font-bold text-gray-900">{activeTotal}</div>
            <div className="text-xs text-gray-500">Active of {totalCount}</div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-3">
            <ProgressRow label="Open" value={open} total={Math.max(activeTotal, 1)} tone="bg-amber-500" rightLabel={`${open || 0}`} />
            <ProgressRow label="Need Review" value={needReview} total={Math.max(activeTotal, 1)} tone="bg-orange-500" rightLabel={`${needReview || 0}`} />
            <ProgressRow label="Closed" value={closed} total={Math.max(totalCount, 1)} tone="bg-emerald-500" rightLabel={`${closed || 0}`} />
          </div>
          <div className="border-t border-gray-100 pt-3">
            <ProgressRow label="Overdue (Open risk)" value={overdue} total={Math.max(activeTotal, 1)} tone="bg-red-500" rightLabel={`${overdue || 0} (${overduePct}%)`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DistributionCard({ title, subtitle, rows, icon: Icon }) {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        {Icon ? <Icon className="h-5 w-5 text-orange-500" /> : null}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
          No data yet.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <ProgressRow
              key={row.label}
              label={row.label}
              value={row.value}
              total={total}
              tone={row.tone || "bg-orange-500"}
              rightLabel={`${row.value} (${percent(row.value, total)}%)`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ForemanWorkloadChart({ rows, onNavigate }) {
  const max = Math.max(1, ...rows.map((row) => Number(row.total || 0)));
  return (
    <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">
            Field workload overview
          </h3>
          <p className="text-xs text-gray-500">
            Total, overdue, and review load by responsible user.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onNavigate?.("issues-control")}
          className="rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-black"
        >
          Open Isues Control
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
          No issue workload is available yet.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.slice(0, 8).map((row) => {
            const width = Math.max(
              4,
              Math.round((Number(row.total || 0) / max) * 100),
            );
            return (
              <div
                key={row.user_id}
                className="rounded-2xl border border-gray-100 bg-gray-50 p-3"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">
                      {row.name || `User #${row.user_id}`}
                    </div>
                    <div className="truncate text-xs text-gray-500">
                      {row.email || "No email"}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-bold text-gray-900">
                      {row.total || 0}
                    </div>
                    <div className="text-[11px] text-gray-500">issues</div>
                  </div>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full bg-orange-500"
                    style={{ width: `${width}%` }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
                  <span className="rounded-full bg-white px-2 py-0.5 text-gray-700">
                    Open {row.open || 0}
                  </span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-blue-700">
                    Responded {row.responded || 0}
                  </span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-orange-700">
                    Review {row.waiting_review || 0}
                  </span>
                  <span className="rounded-full bg-white px-2 py-0.5 text-red-700">
                    Overdue {row.overdue || 0}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PerformanceMetricsMatrix({ rows, groupByGeneralForeman, onNavigate }) {
  return (
    <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-2 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">
            {groupByGeneralForeman
              ? "General Foreman performance matrix"
              : "Lane performance matrix"}
          </h3>
          <p className="text-xs text-gray-500">
            {groupByGeneralForeman
              ? "Exact metrics for each General Foreman branch in your supervised lanes."
              : "Exact metrics for each lane in your reporting scope."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onNavigate?.("issues-control")}
          className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-semibold hover:bg-gray-50"
        >
          Open Issues Control
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="p-5 text-sm text-gray-500">
          {groupByGeneralForeman
            ? "No General Foreman branches are published in your supervised lanes yet."
            : "No published lane metrics are available for this scope yet."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">
                  {groupByGeneralForeman ? "General Foreman" : "Lane"}
                </th>
                <th className="px-4 py-3 text-left">
                  {groupByGeneralForeman ? "Lane" : "Supervisor"}
                </th>
                <th className="px-4 py-3 text-left">Health</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Active</th>
                <th className="px-4 py-3 text-right">Open</th>
                <th className="px-4 py-3 text-right">Need Review</th>
                <th className="px-4 py-3 text-right">Closed</th>
                <th className="px-4 py-3 text-right">Overdue</th>
                <th className="px-4 py-3 text-right">Review Coverage</th>
                <th className="px-4 py-3 text-right">GF Share</th>
                <th className="px-4 py-3 text-right">Supervisor Share</th>
                <th className="px-4 py-3 text-right">QA/QC Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={
                    groupByGeneralForeman
                      ? `gf-${row.general_foreman_user_id}`
                      : `lane-${row.lane_id}`
                  }
                  className="border-t border-gray-100 hover:bg-gray-50/70"
                >
                  <td className="px-4 py-3 font-semibold text-gray-900">
                    {groupByGeneralForeman
                      ? row.general_foreman_name ||
                        `GF #${row.general_foreman_user_id}`
                      : row.lane_name || `Lane #${row.lane_id}`}
                    {groupByGeneralForeman && row.general_foreman_email ? (
                      <div className="mt-0.5 text-[11px] font-normal text-gray-500">
                        {row.general_foreman_email}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {groupByGeneralForeman
                      ? row.lane_name || `Lane #${row.lane_id}`
                      : row.supervisor_name || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${healthBadgeClass(row.health)}`}>
                      {row.health || "No Data"}
                    </span>
                    {Number(row.total || 0) > 0 && row.limited_sample ? (
                      <div className="mt-1 text-[10px] text-gray-400">Limited sample</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">{row.total || 0}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-700">{row.active ?? Math.max(0, Number(row.total || 0) - Number(row.closed || 0))}</td>
                  <td className="px-4 py-3 text-right text-amber-700">{row.open || 0}</td>
                  <td className="px-4 py-3 text-right text-orange-700">{row.need_review ?? row.waiting_review ?? 0}</td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700">{row.closed ?? row.completed ?? 0}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-700">{row.overdue || 0}</td>
                  <td className="px-4 py-3 text-right">{Number(row.review_required || 0) > 0 ? `${Number(row.review_coverage || 0).toFixed(1)}%` : "N/A"}</td>
                  <td className="px-4 py-3 text-right">{Number(row.reviewed || 0) > 0 ? `${Number(row.general_foreman_review_share || 0).toFixed(1)}%` : "N/A"}</td>
                  <td className="px-4 py-3 text-right">{Number(row.reviewed || 0) > 0 ? `${Number(row.supervisor_review_share || 0).toFixed(1)}%` : "N/A"}</td>
                  <td className="px-4 py-3 text-right">{Number(row.reviewed || 0) > 0 ? `${Number(row.qa_qc_review_share || 0).toFixed(1)}%` : "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SupervisorGraphicalDashboardView({
  onNavigate,
  projectRole: projectRoleProp,
}) {
  const {
    activeProject,
    projectRole: contextProjectRole,
    currentUser,
  } = useProjectContext();
  const projectRole = projectRoleProp ?? contextProjectRole;
  const projectId = activeProject?.id;
  const role = norm(projectRole);
  const isGeneralForeman = role === "general_foreman";
  const isSupervisor = role === "supervisor";

  const [dashboard, setDashboard] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportingReport, setExportingReport] = useState(false);

  const loadAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const dashRes = await inspectionsAPI.getSupervisorDashboardSummary(projectId);
      const dashData = dashRes.data || null;
      setDashboard(dashData);
      setFindings(Array.isArray(dashData?.findings) ? dashData.findings : []);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to load supervisor dashboard.",
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const exportSupervisorReport = useCallback(async () => {
    if (!projectId || isGeneralForeman) return;
    setExportingReport(true);
    setError("");
    try {
      const res = await supervisorAPI.supervisorPerformanceReport(projectId);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeProject?.name || "project"}-supervisor-performance-report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to export Supervisor performance report.");
    } finally {
      setExportingReport(false);
    }
  }, [projectId, isGeneralForeman, activeProject?.name]);

  const counts = dashboard?.counts || {};
  const byForeman = Array.isArray(dashboard?.by_foreman)
    ? dashboard.by_foreman
    : [];
  const byLane = Array.isArray(dashboard?.by_lane)
    ? dashboard.by_lane.filter(isOperationalLane)
    : [];
  const byGeneralForeman = Array.isArray(dashboard?.by_general_foreman)
    ? dashboard.by_general_foreman
    : [];

  const analysis = useMemo(() => {
    const all = Array.isArray(findings) ? findings : [];
    const severityMap = new Map();
    const reviewMap = new Map();
    const statusMap = new Map();
    const overdue = [];

    const isClosedIssue = (finding) =>
      ["closed", "completed", "approved"].includes(norm(finding?.status));

    const hasFieldResponse = (finding) => {
      const action = norm(finding?.foreman_action).trim();
      const status = norm(finding?.status);
      if (action === "pending" || status === "pending") return false;
      return Boolean(
        action ||
          finding?.responded_at ||
          ["responded", "answered"].includes(status),
      );
    };

    const isOpenWorkflowIssue = (finding) =>
      ["open", "pending", "reopened"].includes(norm(finding?.status)) &&
      !hasFieldResponse(finding);

    const activeItems = all.filter((finding) => !isClosedIssue(finding));
    const resolvingReview = (finding) =>
      ["approved", "reviewed", "ready_for_closeout"].includes(norm(finding?.review_status));

    all.forEach((finding) => {
      const severity = titleCase(finding.severity || "not set");
      const review = reviewDecisionLabel(finding.review_status || finding.gf_review_status);
      const status = titleCase(finding.status || "not set");
      severityMap.set(severity, (severityMap.get(severity) || 0) + 1);
      reviewMap.set(review, (reviewMap.get(review) || 0) + 1);
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
      if (isPastDate(finding.due_date) && isOpenWorkflowIssue(finding)) {
        overdue.push(finding);
      }
    });

    const toRows = (map, tones) =>
      Array.from(map.entries())
        .map(([label, value], index) => ({
          label,
          value,
          tone: tones[index % tones.length],
        }))
        .sort((a, b) => b.value - a.value);

    const backendDistributions = dashboard?.distributions || {};
    const withTones = (rows, tones) =>
      (Array.isArray(rows) ? rows : []).map((row, index) => ({
        ...row,
        tone: tones[index % tones.length],
      }));

    return {
      workflow: {
        total: Number(counts.total ?? counts.total_foreman_findings ?? all.length),
        active: Number(counts.active ?? activeItems.length),
        open: Number(counts.open ?? 0),
        needReview: Number(counts.need_review ?? counts.waiting_review ?? 0),
        closed: Number(counts.closed ?? 0),
        overdue: Number(counts.overdue ?? overdue.length),
      },
      severityRows: all.length
        ? toRows(severityMap, ["bg-red-500", "bg-orange-500", "bg-blue-500", "bg-emerald-500", "bg-gray-500"])
        : withTones(backendDistributions.severity, ["bg-red-500", "bg-orange-500", "bg-blue-500", "bg-emerald-500", "bg-gray-500"]),
      reviewRows: all.length
        ? toRows(reviewMap, ["bg-gray-500", "bg-blue-500", "bg-orange-500", "bg-red-500", "bg-emerald-500"])
        : withTones(backendDistributions.review, ["bg-gray-500", "bg-blue-500", "bg-orange-500", "bg-red-500", "bg-emerald-500"]),
      statusRows: all.length
        ? toRows(statusMap, ["bg-amber-500", "bg-blue-500", "bg-emerald-500", "bg-gray-500"])
        : withTones(backendDistributions.status, ["bg-amber-500", "bg-blue-500", "bg-emerald-500", "bg-gray-500"]),
      overdue: all.length
        ? overdue.slice(0, 5)
        : (Array.isArray(backendDistributions.overdue_preview) ? backendDistributions.overdue_preview : []),
    };
  }, [findings, dashboard?.distributions, counts.total, counts.total_foreman_findings, counts.active, counts.open, counts.need_review, counts.waiting_review, counts.closed, counts.overdue]);

  if (!activeProject) return null;

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-slate-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-orange-700 shadow-sm ring-1 ring-orange-100">
              <ShieldCheck className="h-3.5 w-3.5" />
              {isGeneralForeman ? "General Foreman Dashboard" : "Supervisor Dashboard"}
            </div>
            <h2 className="mt-3 text-2xl font-bold text-gray-900">
              Welcome back, {getUserName(currentUser)}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <span className="rounded-full border border-gray-200 bg-white px-3 py-1 font-medium text-gray-800">Project: {activeProject.name}</span>
              <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 font-medium text-orange-800">Role: {formatRole(projectRole)}</span>
            </div>
            <p className="mt-3 max-w-3xl text-sm text-gray-600">
              {isGeneralForeman
                ? `Scoped summary of your crew branch, including responses, overdue risk, escalations, and workload for ${activeProject.name}.`
                : `Scoped summary of your reporting line, including response status, review load, overdue risk, and workload for ${activeProject.name}.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isGeneralForeman ? (
              <button
                type="button"
                onClick={exportSupervisorReport}
                disabled={exportingReport}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <FileSpreadsheet className="h-4 w-4" /> {exportingReport ? "Preparing Excel…" : "Export Performance Report"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={loadAll}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.("issues-control")}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600"
            >
              <HardHat className="h-4 w-4" /> Issues Control
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center rounded-3xl border bg-white p-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            <KpiCard label="Total Issues" value={counts.total ?? counts.total_foreman_findings} subtitle="All issues in this reporting scope" icon={ClipboardList} onClick={() => onNavigate?.("issues-control")} tone="orange" />
            <KpiCard label="Active" value={counts.active} subtitle="Everything not closed" icon={TrendingUp} onClick={() => onNavigate?.("issues-control")} tone="blue" />
            <KpiCard label="Open" value={counts.open} subtitle="Awaiting final field response" icon={Clock3} onClick={() => onNavigate?.("issues-control")} tone="blue" />
            <KpiCard label="Need Review" value={counts.need_review ?? counts.waiting_review} subtitle="Final response awaiting authorized review" icon={BadgeCheck} onClick={() => onNavigate?.("issues-control")} tone="emerald" />
            <KpiCard label="Closed" value={counts.closed} subtitle="Authorized resolution/review completed" icon={CheckCircle2} onClick={() => onNavigate?.("issues-control")} tone="emerald" />
            <KpiCard label="Overdue" value={counts.overdue} subtitle="Open issue past due" icon={AlertTriangle} onClick={() => onNavigate?.("issues-control")} tone="red" />
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
            <span className="font-bold uppercase tracking-wide text-gray-500">Metric Legend</span>
            {[
              ["Total", "all issues in this reporting scope"],
              ["Active", "everything not closed"],
              ["Open", "awaiting final field response"],
              ["Need Review", "final response awaiting authorized review"],
              ["Closed", "authorized review/resolution completed"],
              ["Overdue", "Open issue past due"],
            ].map(([label, description]) => (
              <span key={label}><strong>{label}:</strong> {description}</span>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ["Review Coverage", Number(counts.review_required || 0) > 0 ? `${Number(counts.review_coverage || 0).toFixed(1)}%` : "N/A", `${counts.reviewed || 0} of ${counts.review_required || 0} required`],
              ["GF Share", Number(counts.reviewed || 0) > 0 ? `${Number(counts.general_foreman_review_share || 0).toFixed(1)}%` : "N/A", `${counts.reviews_by_general_foreman || 0} reviews`],
              ["Supervisor Share", Number(counts.reviewed || 0) > 0 ? `${Number(counts.supervisor_review_share || 0).toFixed(1)}%` : "N/A", `${counts.reviews_by_supervisor || 0} reviews`],
              ["QA/QC Share", Number(counts.reviewed || 0) > 0 ? `${Number(counts.qa_qc_review_share || 0).toFixed(1)}%` : "N/A", `${counts.reviews_by_qa_qc || 0} reviews`],
            ].map(([label, value, detail]) => (
              <div key={label} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
                <div className="mt-1 text-xs text-gray-500">{detail}</div>
              </div>
            ))}
          </div>

          <PerformanceMetricsMatrix
            rows={isSupervisor ? byGeneralForeman : byLane}
            groupByGeneralForeman={isSupervisor}
            onNavigate={onNavigate}
          />

          <DonutSummary
            total={analysis.workflow.total}
            active={analysis.workflow.active}
            open={analysis.workflow.open}
            needReview={analysis.workflow.needReview}
            closed={analysis.workflow.closed}
            overdue={analysis.workflow.overdue}
          />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <ForemanWorkloadChart rows={byForeman} onNavigate={onNavigate} />
            <DistributionCard
              title="Review status distribution"
              subtitle="Authorized review state across field work."
              rows={analysis.reviewRows}
              icon={ShieldCheck}
            />
            <DistributionCard
              title="Severity distribution"
              subtitle="Risk spread across Field issues."
              rows={analysis.severityRows}
              icon={TrendingUp}
            />
            <DistributionCard
              title="Issue status distribution"
              subtitle="Current issue workflow status."
              rows={analysis.statusRows}
              icon={Users}
            />
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold text-gray-900">
                  Top overdue attention items
                </h3>
                <p className="text-xs text-gray-500">
                  Compact risk preview. Open Issues Control to filter, reassign,
                  or review.
                </p>
              </div>
              <button
                type="button"
                onClick={() => onNavigate?.("issues-control")}
                className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-semibold hover:bg-gray-50"
              >
                Manage in Issues Control
              </button>
            </div>

            {analysis.overdue.length === 0 ? (
              <div className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
                No overdue Field issues right now.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {analysis.overdue.map((finding) => (
                  <div
                    key={finding.id}
                    className="rounded-2xl border border-red-100 bg-red-50/50 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-900">
                          {finding.finding_number || `Issue #${finding.id}`}
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          Inspection ID: {finding.inspection_number || finding.inspection_id || "-"} •{" "}
                          {finding.inspection_type || "-"}
                        </div>
                        <div className="mt-1 line-clamp-2 text-xs text-gray-500">
                          {finding.description || "No description provided."}
                        </div>
                      </div>
                      <div className="shrink-0 rounded-full bg-white px-2 py-1 text-xs font-semibold text-red-700">
                        Due {finding.due_date || "-"}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                      <span className="rounded-full bg-white px-2 py-0.5 text-gray-700">
                        {finding.responsible_user_name || "Unassigned responsible user"}
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-orange-700">
                        {titleCase(finding.severity)}
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-blue-700">
                        {titleCase(finding.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
